import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { fetchJobMembers, fetchActions, fetchJobDetails } from 'actions/job'
import { jobGetMemberGroups, roles, userHasJobRole } from 'utils/business/jobs'
import { jobInitialState } from 'reducers/job'
import { fetchClientActions, fetchClientJob } from 'actions/client'
import { canAccessActionHub } from 'utils/permissions'
import { userPropType } from 'utils/propTypes'

export default function useJob(jobId, isExternalUserParam = false) {
  const dispatch = useDispatch()
  const user = useSelector(state => state.app.user)
  const isExternalUser = user.isExternal || isExternalUserParam

  const getBaseJobState = (state) => {
    return isExternalUser
      ? state.client.jobs[jobId] || jobInitialState
      : state.job
  }
  const job = useSelector(state => getBaseJobState(state))

  const {
    actions,
    analyses,
    engagement,
    members,
    name: jobName,
    clientName,
    packageName,
    packageId,
    _fetchedJobs,
    _fetchingJobDetailsId,
    _fetchedJobDetailsId,
  } = job

  const reachedExecutionLimit = job?.hasExecutionLimit && job?.noOfRuns >= job?.executionLimit
  const showExpiryInfoBanner = job?.hasExpired || reachedExecutionLimit

  const numMembers = members?.length

  const { deloitteTeam, externalTeam } = jobGetMemberGroups(members)

  const clientJobLoaded = job?.jobId?.length > 0

  useEffect(() => {
    if (isExternalUser && !clientJobLoaded && !_fetchedJobs && jobId) {
      dispatch(fetchClientJob(jobId))
    }
  }, [dispatch, jobId, isExternalUser, clientJobLoaded, _fetchedJobs])

  // Loads job info & members on job
  useEffect(() => {
    const isNotExternalUser = jobId && user?._isFetched && !isExternalUser

    if (isNotExternalUser && _fetchingJobDetailsId !== jobId && _fetchedJobDetailsId !== jobId) {
      dispatch(fetchJobDetails({ jobId }))
    }
    if (isNotExternalUser && numMembers === 0 && !job._isFetchingJobMembers) {
      dispatch(fetchJobMembers(jobId))
    }
  }, [
    dispatch,
    jobId,
    user?._isFetched,
    isExternalUser,
    _fetchingJobDetailsId,
    _fetchedJobDetailsId,
    numMembers,
    job._isFetchingJobMembers,
  ])

  const canAccessJobActions = job?.members && user?.permissions
    && canAccessActionHub(user) && !userHasJobRole(user, job, roles.JobExternalReader)

  const isAssignedActionsEditor = job?.members
    && user?.permissions
    && canAccessActionHub(user)
    && userHasJobRole(user, job, roles.ExternalAssignedActionsEditor)

  const isExternalJobEditor = job?.members
    && user?.permissions
    && userHasJobRole(user, job, roles.JobExternalEditor)

  const actionsList = useMemo(() => {
    return (isAssignedActionsEditor
      ? actions?.currentActions?.filter(a => a.totalActions > 0)
      : actions?.currentActions) ?? []
  }, [isAssignedActionsEditor, actions?.currentActions])

  const filteredEntityActions = actions?.currentEntityMetas?.map(entity => ({
    ...entity,
    actions: entity.actions.filter(a => a.totalActions > 0),
  }))

  const entityMetasList = useMemo(() => {
    return (isAssignedActionsEditor
      ? filteredEntityActions?.filter(entity => entity.actions?.length > 0)
      : actions?.currentEntityMetas) ?? []
  }, [isAssignedActionsEditor, actions?.currentEntityMetas, filteredEntityActions])

  const entityMetaMap = useMemo(() => {
    return new Map(entityMetasList?.map(e => [e.entityMeta.id, e.entityMeta]))
  }, [entityMetasList])

  const findEntityMetaName = (entityMetaId) => {
    return entityMetaMap.get(entityMetaId)?.entityMetaName
  }

  // Loads actions for job
  // Todo: find a way to avoid this running multiple times when useJob is called twice close together
  useEffect(() => {
    if (canAccessJobActions && jobId && (!actions) && !job._isFetchingActions && !job._isFetchedActions) {
      if (isExternalUser) {
        dispatch(fetchClientActions(jobId))
      } else {
        dispatch(fetchActions({ jobId }))
      }
    }
  }, [dispatch, canAccessJobActions, jobId, isExternalUser, actions, job._isFetchingActions, job._isFetchedActions])

  const userJobMember = job && job.members && user && user.id
    ? job.members.find(m => m.id === user.id)
    : null

  const userHasJobPermission = (permission) => {
    if (!job || !job.members || !userJobMember || !userJobMember.role || !userJobMember.role.permissions) {
      return false
    }
    return userJobMember.role.permissions.map(p => p.name).includes(permission)
  }

  const goToInternalJob = (tab = 'details') => {
    dispatch(push(`/job/${jobId}/${tab}`))
  }

  const goToClientJob = (tab) => {
    const suffix = isAssignedActionsEditor ? 'actions' : tab
    dispatch(push(`/insights/${packageId}/${jobId}/${suffix}`))
  }

  const goToInternalReport = (analysisId) => {
    dispatch(push(`/report/${jobId}/${analysisId}`))
  }

  const goToClientReport = (analysisId) => {
    dispatch(push(`/insights/${packageId}/report/${jobId}/${analysisId}`))
  }

  const goToJob = (tab = 'details') => {
    const userDoesntHaveRole = (!userHasJobRole(user, job, roles.JobExternalEditor) || !userHasJobRole(user, job, roles.JobExternalActionEditor))
    if (isExternalUser && userDoesntHaveRole) {
      goToClientJob(tab)
    } else {
      goToInternalJob(tab)
    }
  }

  const goToReport = (analysisId) => {
    if (isExternalUser) {
      goToClientReport(analysisId)
    } else {
      goToInternalReport(analysisId)
    }
  }

  const getBreadcrumbs = ({ isExternal, extraCrumbs = [] }) => {
    const externalBreadcrumbs = isExternal
      ? [{
        key: 'package',
        label: packageName,
        onClick: () => dispatch(push(`/insights/${packageId}`)),
      }]
      : []
    const internalBreadcrumbs = !isExternal && engagement && engagement.name !== 'Unallocated'
      ? [{
        key: 'engagement',
        label: engagement && engagement.name,
      }]
      : []

    return [
      ...externalBreadcrumbs,
      ...internalBreadcrumbs,
      {
        key: 'job',
        label: jobName,
        onClick: goToJob,
      },
      ...extraCrumbs,
    ]
  }

  const showPackageOption = (numActions, currentEntityMeta, selectedEntityMeta) => {
    let showPackage = true

    if (selectedEntityMeta !== null) {
      // only show actionsets within entity
      showPackage = currentEntityMeta === selectedEntityMeta
    } else {
      // only show actionsets without entity
      showPackage = currentEntityMeta === null
    }
    // only show actionsets with assigned tasks
    if (isAssignedActionsEditor) {
      showPackage = numActions > 0
    }
    return showPackage
  }

  const getNavigationOptions = (actionSet, isCurrent, goToOtherAction) => {
    // Header config
    const currentActions = (actions && actions.currentActions) || []
    const reportActionButton = !isAssignedActionsEditor && actionSet.analysisName
      ? {
        label: 'View report',
        onClick: () => goToReport(actionSet.analysisName),
      } : null

    const selectedEntityMetaId = actionSet.entityMetaId

    const packageOptions = (actions && actions.currentActions
      .filter(a => showPackageOption(a.totalActions, a.actionSet.entityMetaId, selectedEntityMetaId))
      .map(a => ({
        value: a.actionSet.id,
        label: a.displayName,
        entity: findEntityMetaName(a.actionSet.entityMetaId),
        onSelect: () => goToOtherAction(a.actionSet.id),
      }))) || []

    const analysisOptions = (!isAssignedActionsEditor
      && entityMetasList?.length === 0
      && analyses.filter(fa => fa.hasData).map((a) => {
        const action = currentActions.find(x => x.actionSet.analysisName === a.id)
        return {
          value: action ? action.actionSet.id : a.id,
          label: a.name,
          entity: findEntityMetaName(action?.actionSet?.entityMetaId),
          onSelect: action
            ? () => goToOtherAction(action.actionSet.id)
            : () => goToReport(a.id),
        }
      })) || []

    const options = [
      ...packageOptions,
      ...analysisOptions,
    ]

    const solutionsTitle = selectedEntityMetaId ? findEntityMetaName(selectedEntityMetaId) : 'Action sets'

    let optionGroups = []
    if (!isCurrent) {
      optionGroups = null
    } else {
      if (packageOptions.length > 0) {
        optionGroups.push({ name: solutionsTitle, values: packageOptions.map(x => x.value) })
      }
      if (analysisOptions.length > 0) {
        optionGroups.push({ name: 'Analyses', values: analysisOptions.map(x => x.value) })
      }
    }

    return {
      options, optionGroups, packageOptions, analysisOptions, reportActionButton,
    }
  }

  return {
    job,
    analyses,
    deloitteTeam,
    externalTeam,
    jobName,
    clientName,
    packageName,
    packageId,
    actions,
    actionsList,
    entityMetasList,
    engagement,
    canAccessJobActions,
    isAssignedActionsEditor,
    isExternalJobEditor,
    userJobMember,
    goToJob,
    goToReport,
    getBreadcrumbs,
    getNavigationOptions,
    userHasJobPermission,
    reachedExecutionLimit,
    showExpiryInfoBanner,
  }
}

export const useJobPropTypes = PropTypes.shape({
  deloitteTeam: PropTypes.arrayOf(userPropType),
  externalTeam: PropTypes.arrayOf(userPropType),
  isAssignedActionsEditor: PropTypes.bool,
  userHasJobPermission: PropTypes.func,
})
